import { useState } from 'react';

import { TriangleAlertIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { selectAccountSlug } from '@/redux/session/selectors';
import { trackEvent } from '@/segment/segment';
import { EventName } from '@/shared/types/segment';
import { isKeyEnter } from '@/util/util';

import { useCampaigns } from '../../hooks/useCampaigns';

interface DeleteCampaignModalProps {
  modalCampaignId: string;
  setModalCampaignId: (modalCampaignId: string | null) => void;
  filter?: string;
}

export const DeleteCampaignModal = ({
  modalCampaignId,
  setModalCampaignId,
  filter,
}: DeleteCampaignModalProps) => {
  const slug = useSelector(selectAccountSlug);
  const { campaign, deleteCampaignAsync, deleteStatus, updateCampaignAsync, updateStatus } =
    useCampaigns(modalCampaignId, filter);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [confirmText, setConfirmText] = useState('');

  const confirmWord = 'Delete';
  const isRunning = campaign?.status === 'in_progress';
  const isConfirmed = confirmText.toLowerCase() === confirmWord.toLowerCase();
  const isLoading = deleteStatus === 'pending' || updateStatus === 'pending';

  const closeModal = () => {
    setConfirmText('');
    setModalCampaignId(null);
  };

  const handleDelete = async () => {
    trackEvent(EventName.ConfirmDeleteCampaign, { campaign_id: modalCampaignId });
    try {
      if (isRunning) {
        await updateCampaignAsync({ campaign_id: modalCampaignId, status: 'canceled' });
      }
      await deleteCampaignAsync({ campaign_id: modalCampaignId, name: campaign?.name });
      closeModal();
      navigate(`/${slug}/campaigns`, { replace: true });
    } catch {
      closeModal();
    }
  };

  return (
    <Dialog open={!!modalCampaignId} onOpenChange={closeModal}>
      <DialogContent className="gap-6 sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('campaigns.delete_campaign')}</DialogTitle>
          <DialogDescription>
            {t('campaigns.delete_campaign_subtitle', { 0: campaign?.name })}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2">
          <Label htmlFor="confirm-delete">
            {t('campaigns.type_delete_to_confirm', { 0: confirmWord })}
          </Label>
          <Input
            id="confirm-delete"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            onKeyDown={(e) => {
              if (isKeyEnter(e) && isConfirmed && !isLoading) {
                e.preventDefault();
                handleDelete();
              }
            }}
            autoComplete="off"
          />
        </div>

        {isRunning && (
          <Alert variant="warning">
            <TriangleAlertIcon />
            <AlertTitle>{t('campaigns.stop_and_delete_title')}</AlertTitle>
            <AlertDescription>{t('campaigns.stop_and_delete_warning')}</AlertDescription>
          </Alert>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="outline-neutral" onClick={closeModal}>
            {t('common.back_to_safety')}
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={!isConfirmed}
            isLoading={isLoading}
          >
            {t('common.delete')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
